import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Mail, CheckCircle, Calendar } from 'lucide-react';
import Pagination from './Pagination';

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [paginationData, setPaginationData] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [resolveId, setResolveId] = useState(null);

  const fetchContacts = async (currentPage = 1) => {
    try {
      setLoading(true);
      const res = await api.get(`/api/admin/contacts?page=${currentPage}&limit=10`);
      setContacts(res.data.data);
      setPaginationData(res.data.pagination);
    } catch (err) {
      console.error('Error fetching contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts(page);
  }, [page]);

  const handleResolve = (id) => {
    setResolveId(id);
  };

  const confirmResolve = async () => {
    if (!resolveId) return;
    const id = resolveId;
    setResolveId(null);
    setActionLoading(true);
    try {
      await api.post(`/api/admin/contacts/${id}/resolve`);
      await fetchContacts(page);
    } catch (err) {
      alert(`Error resolving query: ${err.response?.data?.message || err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-8 sm:space-y-10">
      {/* Page header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Customer Queries</h1>

        {actionLoading && (
          <div className="flex items-center gap-2 text-sm text-primary-600 bg-primary-50 p-2 px-4 rounded-xl">
            <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
            Processing...
          </div>
        )}
      </div>

      {/* Table / Card View */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Desktop Table View */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-left min-w-[560px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Date</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer Info</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Query / Message</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-10 text-center text-gray-500">Loading queries...</td>
                </tr>
              ) : contacts.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50 transition-colors group">
                  <td className="px-4 sm:px-6 py-4 align-top hidden sm:table-cell">
                    <div className="flex items-center gap-2 text-gray-500 text-sm whitespace-nowrap">
                      <Calendar size={14} />
                      {new Date(c.createdAt).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-4 align-top">
                    <div className="font-semibold text-gray-900 text-sm">{c.name}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1.5 mt-1">
                      <Mail size={11} />
                      <a href={`mailto:${c.email}`} className="hover:text-primary-600 break-all">{c.email}</a>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-4 min-w-[220px] sm:min-w-[280px] max-w-sm">
                    <div className="font-bold text-gray-900 text-sm mb-1">{c.subject}</div>
                    <p className="text-xs sm:text-sm text-gray-600 whitespace-pre-wrap line-clamp-3">{c.message}</p>
                  </td>
                  <td className="px-4 sm:px-6 py-4 align-top">
                    {c.isResolved ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase bg-gray-100 text-gray-600 border border-gray-200 whitespace-nowrap">
                        <CheckCircle size={12} />
                        Resolved
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap">
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></div>
                        Pending
                      </span>
                    )}
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-right align-top">
                    {!c.isResolved && (
                      <button
                        onClick={() => handleResolve(c.id)}
                        disabled={actionLoading}
                        className="text-primary-600 text-xs sm:text-sm font-semibold hover:text-primary-700 transition-colors bg-primary-50 px-2.5 sm:px-3 py-1.5 rounded-lg hover:bg-primary-100 disabled:opacity-50 whitespace-nowrap"
                      >
                        Mark Resolved
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && contacts.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-10 text-center text-gray-500">
                    No customer queries found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="sm:hidden divide-y divide-gray-100">
          {loading ? (
            <div className="p-6 text-center text-gray-500">Loading queries...</div>
          ) : contacts.map((c) => (
            <div key={c.id} className="p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-bold text-gray-900">{c.name}</h4>
                  <p className="text-[10px] text-gray-400 uppercase font-bold mt-0.5">{new Date(c.createdAt).toLocaleDateString()}</p>
                </div>
                {c.isResolved ? (
                  <span className="bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border border-gray-200">Resolved</span>
                ) : (
                  <span className="bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border border-amber-200">Pending</span>
                )}
              </div>

              <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                <p className="text-xs font-bold text-gray-900 mb-1">{c.subject}</p>
                <p className="text-xs text-gray-600 line-clamp-4">{c.message}</p>
              </div>

              <div className="flex items-center justify-between pt-1">
                <a href={`mailto:${c.email}`} className="text-xs text-primary-600 font-medium flex items-center gap-1.5">
                  <Mail size={12} />
                  {c.email}
                </a>
                {!c.isResolved && (
                  <button
                    onClick={() => handleResolve(c.id)}
                    className="text-xs bg-primary-600 text-white px-3 py-1.5 rounded-lg font-bold shadow-sm"
                  >
                    Resolve
                  </button>
                )}
              </div>
            </div>
          ))}
          {!loading && contacts.length === 0 && (
            <div className="p-6 text-center text-gray-500">No queries found.</div>
          )}
        </div>

        <Pagination pagination={paginationData} onPageChange={setPage} />
      </div>

      {/* Resolve Confirmation Modal */}
      {resolveId && (
        <div className="fixed w-full h-full inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 text-center transform transition-all animate-in fade-in zoom-in duration-200">
            <div className="w-16 h-16 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Mark as Resolved?</h3>
            <p className="text-gray-500 mb-6 text-sm">
              Are you sure you want to mark this customer query as resolved?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setResolveId(null)}
                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmResolve}
                className="flex-1 px-4 py-2.5 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts;
